import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Button from '../../../components/Button/Button';
import CircledIcon from '../../../components/CircledIcon';
import GenericCard from '../../../components/GenericCards/GenericCards';
import useMobile from '../../../hooks/useMobileHook';
import PopoverWithLinks from '../PopoverLinks/PopoverLinks';

const otherCompanyBenefitsOptions = [
  {
    linkHeader: 'Call 1-888-888-8888',
    route: '#',
    icon: light('phone'),
    disableRightIcon: true,
    types: ['Commuter Benefits', 'Caregiver Assistance', 'Student Loan Assistance'],
  },
  {
    linkHeader: 'Call 1-800-438-6388',
    route: '#',
    icon: light('phone'),
    disableRightIcon: true,
    types: ['Home & Auto', 'Pet Insurance'],
  },
  {
    linkHeader: 'Call 1-888-923-6236',
    route: '#',
    icon: light('phone'),
    disableRightIcon: true,
    types: ['Purchase Program'],
  },
  {
    linkHeader: 'Call 1-866-664-4621',
    route: '#',
    icon: light('phone'),
    disableRightIcon: true,
    types: ['Employee Discounts'],
  },
  {
    linkHeader: 'Website',
    route: `http://commuterbenefits.com/`,
    icon: light('globe'),
    rightIcon: light('external-link'),
    types: ['Commuter Benefits'],
    target: '_blank',
  },
  {
    linkHeader: 'Website',
    route: `http://mybenefits.metlife.com/`,
    icon: light('globe'),
    rightIcon: light('external-link'),
    types: ['Home & Auto', 'Pet Insurance'],
    target: '_blank',
  },
  {
    linkHeader: 'Website',
    route: `http://purchasingpower.com/`,
    icon: light('globe'),
    rightIcon: light('external-link'),
    types: ['Purchase Program'],
    target: '_blank',
  },
  {
    linkHeader: 'Website',
    route: `http://benefithub.com/`,
    icon: light('globe'),
    rightIcon: light('external-link'),
    types: ['Employee Discounts'],
    target: '_blank',
  },
  {
    linkHeader: 'Website',
    route: `https://ianacare.com`,
    icon: light('globe'),
    rightIcon: light('external-link'),
    types: ['Caregiver Assistance'],
    target: '_blank',
  },
  {
    linkHeader: 'Website',
    route: `https://getfiducius.com`,
    icon: light('globe'),
    rightIcon: light('external-link'),
    types: ['Student Loan Assistance'],
    target: '_blank',
  },
  {
    linkHeader: 'Plan Info',
    route: `${process.env.PUBLIC_URL}/${'blank-demo-example.pdf'}`,
    icon: light('file-pdf'),
    rightIcon: light('external-link'),
    types: [
      'Commuter Benefits', 
      'Home & Auto', 
      'Pet Insurance', 
      'Purchase Program', 
      'Employee Discounts', 
      'Caregiver Assistance', 
      'Student Loan Assistance'
    ],
    target: '_blank',
  },
];

const OtherCompanyBenefits = ({
  companyBenefits,
}: any) => {
  const isMobile = useMobile();

  const [isShown, setIsShown] = useState(false);
  const handleIsShown = () => {
    setIsShown((prev) => !prev);
  };

  const benefits = useMemo(() => {
    return isShown ? companyBenefits : companyBenefits.slice(0, 3);
  }, [isShown, companyBenefits]);

  const icon = (iconName: IconProp) => (
    <CircledIcon
      variant="icon-emphasized"
      size={isMobile ? 'icon-sm' : 'icon-lg'}
      icon={iconName}
    />
  );

  const getCardIcon = (type: String) => {
    switch (type) {
      case 'car-side':
        return light('car-side');
      case 'house':
        return light('house');
      case 'dog':
        return light('dog');
      case 'money-check-dollar':
        return light('money-check-dollar');
      case 'badge-percent':
        return light('badge-percent');
      case 'hands-holding-child':
        return light('hands-holding-child');
      case 'hand-holding-dollar':
        return light('hand-holding-dollar');
      default:
        return light('house');
    }
  };

  return (
    <Row className="m-0 p-0 d-flex justify-content-between">
      <Row className={'m-0 p-0'}>
        <Col
          className={
            'p-0 d-flex justify-content-between align-items-center mb-10'
          }
        >
          <h4 className={'text-sm-20-lg-32 m-0 lh-xs'}>{'Other Company Benefits'}</h4>
        </Col>
      </Row>

      <Container className={'p-0 border rounded-1'}>
        <GenericCard className='pb-10' variant={'transperant'}>
          <Row className="m-0 px-6 px-lg-0">
            <Row className="px-0 mx-0 mb-3 pb-3 border-bottom">
              <Col 
                className="col-6 px-0 text-start text-sm-12-lg-14 fw-light text-ligther-grey"
              >
                Plan
              </Col>
            </Row>
            {benefits.map(
              (snapshot: any, index: number) => (
                <Row
                  key={index}
                  className={`px-0 mx-0 pb-3 ${benefits.length-1 !== index ? 'mb-3' : ''} border-bottom`}
                >
                  <Col className="col-lg-6 px-0 text-start text-sm-12-lg-14 fw-light text-ligther-grey">
                    <Row className="m-0">
                      <Col className="p-0 col-12 col-lg-12 d-flex">
                        {icon(getCardIcon(snapshot['icon']))}
                        <div className="d-flex flex-column justify-content-center ps-4">
                          <div className="fw-normal text-sub1">
                            <span>{snapshot['title']}</span>
                          </div>
                          <div className="mt-1 fw-lighter text-caption">
                            <span>{snapshot['description']}</span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                  <Col className="col-lg-6 mt-4 mt-lg-0 px-0 text-end text-sm-12-lg-14 fw-light text-ligther-grey">
                    <PopoverWithLinks
                      linksData={otherCompanyBenefitsOptions.filter(
                      (option) =>
                        option.types.includes(snapshot['title'])
                      )}
                    />
                  </Col>
                </Row>
              )
            )}            
          </Row>
          {companyBenefits.length > 3 && (
            <Container className={'col-12 text-end px-4 px-lg-0 mt-10 mb-0'}>
              <Button onClick={handleIsShown} size={'sm'}>
                Show {!isShown ? 'more' : 'less'}
              </Button>
            </Container>
          )}
        </GenericCard>
      </Container>
    </Row>
  );
};

export default OtherCompanyBenefits;
